import { Swiper, SwiperSlide } from "swiper/react/swiper-react.js";
import "swiper/swiper.scss"; // core Swiper
import "swiper/modules/navigation/navigation.scss"; // Navigation module
import "swiper/modules/pagination/pagination.scss"; // Pagination module
import "swiper/modules/autoplay/autoplay.scss"; // Autoplay module
import { Pagination, Autoplay } from "swiper";
import { PageScreenNavigationProp, routePage, routeTitleDetails, routeTrailer } from "../../Routes";
import { useNavigation } from "@react-navigation/native";
import Image from "../../components/Image/Image";
import { useEffect, useState } from "react";
import TouchableOpacity from "../../components/TouchableOpacity/TouchableOpacity";
import { calculateImageWidth, convertARGBtoRGBA, getInitialScale } from "../../utils/Utils";
import useGenericContentStore from "../../store/genericContent.store";
import { Pages } from "../../Types";
import { HeroProps } from "./model";
import { FeatureLineOne, FeatureLineTwo, FeatureText, StyledHeroContainer, StyledImageBanner, StyledMain, TextContainer } from "./styled";
import useScreenDimensions from "../../hooks/useScreenDimensions";
import { dark_gray, styleHelper } from "../../StyleHelpers";

const heroBannerStyles = {
	marginLeft: 152,
	marginRight: 20,
	borderRadius: 16,
	aspectRatio: 4 / 1,
	outlineStyle: "solid",
	outlineColor: "rgba(255, 255, 255, 0.30)",
	outlineWidth: 2,
	outlineOffset: -2,
	backgroundColor: dark_gray,
};

const heroImageStyleNarrow = {
	width: "31.0625rem",
	height: "5.0625rem",
	flexShrink: 0,
};

const heroImageStyle = {
	width: "36.5625rem",
	height: "5.9325rem",
	flexShrink: 0,
};

const HeroBanner = (props: HeroProps) => {
	const delay = 8000;
	const navigationTitleScreen = useNavigation<any>();
	const navigationPageScreen = useNavigation<PageScreenNavigationProp>();
	const [sttLoaded, setSttLoaded] = useState(false);
	const { width } = useScreenDimensions();
	const { setNavigateEvent, setSelectedTabs, isNarrow, setIsExtras } = useGenericContentStore((state: any) => ({
		setNavigateEvent: state.setNavigateEvent,
		setSelectedTabs: state.setSelectedTabs,
		setIsExtras: state.setIsExtras,
		isNarrow: state.isNarrow,
	}));

	/**
	 * Returns the height for a banner image based on screen width.
	 * - Uses a fixed height for narrow screens.
	 * - Returns "auto" for wider screens.
	 *
	 * @returns string | number - The calculated banner height.
	 */
	const getHeight = () => (isNarrow ? styleHelper.wide.banner.height : "auto");

	/**
	 * Styles for the banner image on narrow screens.
	 * - Width is fixed.
	 * - Height is determined by `getHeight()`.
	 * - Aspect ratio is fixed at 4:1.
	 */
	const styleImageNarrow = { width: "54rem", height: getHeight(), aspectRatio: "4/1" };

	/**
	 * Handles trailer navigation.
	 * - Extracts `parentProductId` and `streamUrl` from the trailer object.
	 * - Navigates to the trailer screen with appropriate parameters.
	 * @param trailer
	 */
	const onTrailer = async (trailer: any) => {
		const parentProductId = trailer.id;
		const streamUrl = trailer.url;

		navigationTitleScreen.navigate(
			routeTrailer,
			props.isPreview ? { parentProductId, streamUrl, preview: true } : { parentProductId, streamUrl },
		);
	};

	/**
	 * Handles navigation based on the type of link clicked.
	 * - Sets the selected tab to 0.
	 * - If in preview mode, sets the navigation event to `Pages.preview`.
	 * - Navigates to the appropriate screen:
	 *   - "ParentProduct": navigates to the title details page, including preview flag if applicable.
	 *   - "Page": navigates to a page with the given unique ID.
	 * 	 - "Trailer": navigates to the trailer page with the given unique ID.
	 *
	 * @param navigationLinkType - Type of the navigation link ("ParentProduct" | "Page").
	 * @param navigationLink - The ID or value associated with the navigation target.
	 */
	const onPress = (navigationLinkType: string, navigationLink: any, featureTrailer: any) => {
		if (props?.isPreview) setNavigateEvent(Pages.preview);
		setIsExtras(false);
		setSelectedTabs(0);

		const previewParams = props?.isPreview ? { preview: true } : {};

		switch (navigationLinkType) {
			case "ParentProduct":
				navigationTitleScreen.push(routeTitleDetails, { parentProductId: navigationLink, ...previewParams });
				break;
			case "Page":
				navigationPageScreen.push(routePage, { uniqueId: navigationLink });
				break;
			case "Trailer":
				onTrailer(featureTrailer);
				break;
		}
	};
	/**
	 * Calculates the number of items to display based on screen width.
	 * - Returns 1.83 for wide screens.
	 * - For narrow screens, returns a ratio of the screen width to 864.
	 */
	const calculateItemsByWidth = () => {
		if (!isNarrow) return 1.83;
		return width / (864 * getInitialScale());
	};

	/**
	 * Renders feature text or STT image for a given object.
	 * - Filters out any feature text items without `text`.
	 * - Returns null if there is neither feature text nor an STT URL.
	 * - Extracts "Line1" and "Line2" from the feature text array.
	 * - Determines layout styles based on `isNarrow`.
	 * - Renders:
	 *   - STT image if `sttUrl` exists.
	 *   - Otherwise, renders Line1 text.
	 *   - Line2 text if it exists.
	 *
	 * @param obj - Object containing `featureText` array and optional `sttUrl`.
	 * @returns JSX element for feature text or STT image, or null if nothing to render.
	 */
	const renderFeatureText = (obj: any) => {
		const featureText = obj.featureText?.filter((f: any) => f.text) ?? [];
		const stt = obj?.sttUrl;

		if (!featureText.length && !stt) return null;

		const lineOne = featureText.find((f: any) => f.name === "Line1");
		const lineTwo = featureText.find((f: any) => f.name === "Line2");

		const stylesLayout = isNarrow ? styleHelper.narrow.banner : styleHelper.wide.banner;

		return (
			<FeatureText height={stylesLayout.height} isNarrow={isNarrow} isNoBgColor={!stt && !lineOne.text.trim() && !lineTwo.text.trim()}>
				<TextContainer>
					{stt ? (
						<Image
							source={{
								uri: `${stt}?width=${calculateImageWidth(false, true)}&disablewebp=false`,
							}}
							resizeMode="contain"
							style={isNarrow ? heroImageStyleNarrow : heroImageStyle}
						/>
					) : (
						lineOne.text.trim() && (
							<FeatureLineOne
								fontSize={stylesLayout.lineOne.fontSize}
								lineHeight={stylesLayout.lineOne.lineHeight}
								hexCode={convertARGBtoRGBA(lineOne.hexCode)}
							>
								{lineOne.text}
							</FeatureLineOne>
						)
					)}

					<FeatureLineTwo
						fontSize={stylesLayout.lineTwo.fontSize}
						lineHeight={stylesLayout.lineTwo.lineHeight}
						hexCode={convertARGBtoRGBA(lineTwo.hexCode)}
					>
						{lineTwo.text}
					</FeatureLineTwo>
				</TextContainer>
			</FeatureText>
		);
	};

	/**
	 * Marks STT as loaded whenever `heroBanner` prop changes.
	 */
	useEffect(() => {
		setSttLoaded(true);
	}, [props.heroBanner]);

	return (
		//@ts-ignore
		<StyledHeroContainer>
			{/* @ts-ignore */}
			<StyledMain>
				<Swiper
					style={{ width: "100%" }}
					slidesPerView={isNarrow ? calculateItemsByWidth() : 1}
					centeredSlides={true}
					loop={isNarrow}
					spaceBetween={isNarrow ? 30 : 1}
					pagination={{
						clickable: true,
					}}
					allowTouchMove={true}
					autoplay={{
						delay: delay,
						disableOnInteraction: false,
					}}
					modules={[Autoplay, Pagination]}
				>
					{props?.heroBanner?.map((obj: any, index: number) => {
						return (
							<SwiperSlide key={`hero-banner${obj.featureId}`} tabIndex={index}>
								<TouchableOpacity
									activeOpacity={1}
									onPress={() => onPress(obj?.navigationLinkType, obj?.navigationLink, obj?.featureTrailer)}
								>
									{renderFeatureText(obj)}
									{sttLoaded ? (
										<StyledImageBanner
											imageBanner={`${obj?.featureImageUrl}?width=${calculateImageWidth(false, true)}&disablewebp=false`}
											style={[isNarrow && styleImageNarrow, heroBannerStyles, isNarrow && { marginLeft: 1 }]}
										/>
									) : (
										<></>
									)}
								</TouchableOpacity>
							</SwiperSlide>
						);
					})}
				</Swiper>
			</StyledMain>
		</StyledHeroContainer>
	);
};

export default HeroBanner;
